import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import QuratorLayout from '../components/qurator/QuratorLayout';
import PendingValidationSlider from '../components/qurator/PendingValidationSlider';
import API from '../config/api';

/* ─── Portal Tooltip ─── */
const Tooltip = ({ text, children, position = 'top' }) => {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, pos: 'top' });

  const show = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const left = rect.left + rect.width / 2;
    const top  = position === 'bottom' ? rect.bottom + 10 : rect.top - 10;
    setCoords({ top, left, pos: position === 'bottom' ? 'bottom' : 'top' });
    setVisible(true);
  }, [position]);

  const hide = useCallback(() => setVisible(false), []);

  const child = React.Children.only(children);
  const enhanced = React.cloneElement(child, {
    onMouseEnter: (e) => { show(e); child.props.onMouseEnter?.(e); },
    onMouseLeave: (e) => { hide();  child.props.onMouseLeave?.(e); },
    onFocus:      (e) => { show(e); child.props.onFocus?.(e); },
    onBlur:       (e) => { hide();  child.props.onBlur?.(e); },
  });

  return (
    <>
      {enhanced}
      {visible && ReactDOM.createPortal(
        <div className={`lib-tooltip lib-tooltip-${coords.pos}`}
          style={{ top: coords.top, left: coords.left }}>
          {text}
        </div>,
        document.body
      )}
    </>
  );
};

/* ─── Library Page ─── */
const Library = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ trainer: 0, published: 0 });

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    try {
      const res = await API.get('/qurator/library/stats');
      if (res.data.success) setStats(res.data.stats);
    } catch (e) {
      console.error('Library stats error:', e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <QuratorLayout>
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center',
          justifyContent:'center', minHeight:'60vh', gap:'1rem', color:'#718096' }}>
          <div className="qrt-loading"></div>
          <p>Loading library...</p>
        </div>
      </QuratorLayout>
    );
  }

  return (
    <QuratorLayout>
      <div className="container-fluid px-4 py-4">

        {/* ── Hero ── */}
        <div className="lib-hero mb-4">
          <div className="lib-hero-inner">
            <div>
              <h1 className="lib-hero-title">
                <i className="bi bi-journals"></i> Experiment Library
              </h1>
              <p className="lib-hero-sub">
                Create, manage and publish experiment copilots and training simulations for your research teams.
              </p>
            </div>
            <i className="bi bi-journal-bookmark-fill lib-hero-bg-icon d-none d-lg-block"></i>
          </div>
        </div>

        <PendingValidationSlider
          title="Needs human validation"
          subtitle="Review and validate experiment steps before publishing — most recent first."
          className="lib-pending-validation"
        />

        {/* ── Section Heading ── */}
        <div className="lib-section-heading mb-3">
          <i className="bi bi-lightning-charge-fill"></i>
          <span>Manage Your Library</span>
        </div>

        {/* ── Action Cards ── */}
        <div className="row g-4 mb-4">

          {/* Draft Simulations */}
          <div className="col-12 col-lg-4">
            <div className="lib-card lib-card-draft">
              <div className="lib-card-top">
                <div className="lib-card-icon lib-icon-orange">
                  <i className="bi bi-pencil-square"></i>
                </div>
                <div>
                  <h5 className="lib-card-title">Draft Simulations</h5>
                  <p className="lib-card-desc">
                    Build and edit new experiment copilots. Organise your drafts by project before publishing them to your team.
                  </p>
                  <div className="lib-tags">
                    <span className="lib-tag"><i className="bi bi-pencil"></i> Drafting</span>
                    <span className="lib-tag"><i className="bi bi-folder2"></i> By Project</span>
                  </div>
                </div>
              </div>
              <div className="lib-card-footer">
                <Tooltip text="Open drafting workspace to create or edit experiment copilots">
                  <Link to="/drafts?project_id=0&type=mr" className="lib-btn lib-btn-primary">
                    <i className="bi bi-pencil-square"></i> Open Drafts
                  </Link>
                </Tooltip>
              </div>
            </div>
          </div>

          {/* Trainer Simulations */}
          <div className="col-12 col-lg-4">
            <div className="lib-card lib-card-trainer">
              <div className="lib-card-top">
                <div className="lib-card-icon lib-icon-amber">
                  <i className="bi bi-play-circle-fill"></i>
                </div>
                <div>
                  <h5 className="lib-card-title">Trainer Simulations</h5>
                  <p className="lib-card-desc">
                    Review and run trainer-mode experiments. Record sessions as a Qurator or Trainer before publishing them for Qure users.
                  </p>
                  <div className="lib-tags">
                    <span className="lib-tag lib-tag-amber">
                      <i className="bi bi-play-circle"></i> {stats.trainer} Simulation{stats.trainer !== 1 ? 's' : ''}
                    </span>
                    <span className="lib-tag"><i className="bi bi-camera-video"></i> Ready to Record</span>
                  </div>
                </div>
              </div>
              <div className="lib-card-footer">
                <Tooltip text="View all trainer simulations and manage their assignments to users">
                  <Link to="/trainer-simulations" className="lib-btn lib-btn-amber">
                    <i className="bi bi-play-circle"></i> View Simulations
                  </Link>
                </Tooltip>
              </div>
            </div>
          </div>

          {/* Published Copilots */}
          <div className="col-12 col-lg-4">
            <div className="lib-card lib-card-published">
              <div className="lib-card-top">
                <div className="lib-card-icon lib-icon-green">
                  <i className="bi bi-patch-check-fill"></i>
                </div>
                <div>
                  <h5 className="lib-card-title">Published Copilots</h5>
                  <p className="lib-card-desc">
                    Copilots reviewed, approved and published — accessible to all assigned Qure users in your organisation.
                  </p>
                  <div className="lib-tags">
                    <span className="lib-tag lib-tag-green">
                      <i className="bi bi-patch-check"></i> {stats.published} Published
                    </span>
                    <span className="lib-tag"><i className="bi bi-globe"></i> Live &amp; Active</span>
                  </div>
                </div>
              </div>
              <div className="lib-card-footer">
                <Tooltip text="Browse all published copilots available to Qure users">
                  <Link to="/published-copilots" className="lib-btn lib-btn-green">
                    <i className="bi bi-patch-check"></i> View Published
                  </Link>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* ── FAB ── */}
      <Tooltip text="Create a new experiment copilot">
        <Link to="/drafts?project_id=0" className="lib-fab" title="New Copilot">
          <i className="bi bi-plus-lg"></i>
        </Link>
      </Tooltip>

    </QuratorLayout>
  );
};

export default Library;
